import { apiGetNotifications, apiPollNotifications } from '@/services/endpoints/notifications'

const POLL_INTERVAL_MS = 10000
const PAGE_SIZE = 50

const state = () => ({
  notifications: [],
  newNotifications: 0,
  newNotificationIds: [],
  lastVersion: 0,
  lastSeenTimestamp: parseInt(localStorage.getItem('@sos:notifications:lastSeenTs')) || 0,
  pollInterval: null,
  isPolling: false,
  hasMore: false,
  loadingMore: false,
})

const mutations = {
  SET_NOTIFICATIONS (state, { notifications, hasMore }) {
    state.notifications = notifications
    state.hasMore = hasMore
  },

  APPEND_NOTIFICATIONS_BOTTOM (state, { notifications, hasMore }) {
    const existingIds = new Set(state.notifications.map(n => n.id))
    const unique = notifications.filter(n => !existingIds.has(n.id))
    state.notifications = [...state.notifications, ...unique]
    state.hasMore = hasMore
  },

  APPEND_NOTIFICATIONS_TOP (state, newItems) {
    state.notifications = [...newItems, ...state.notifications]
      .sort((a, b) => b.unixtimestamp - a.unixtimestamp)
  },

  SET_NEW_NOTIFICATIONS (state, count) {
    state.newNotifications = count
  },

  RESET_NEW_NOTIFICATIONS (state) {
    state.newNotifications = 0
    state.newNotificationIds = []
  },

  ADD_NEW_NOTIFICATION_IDS (state, ids) {
    state.newNotificationIds = [...state.newNotificationIds, ...ids]
  },

  MARK_NEW_SINCE_LAST_SEEN (state, notifications) {
    if (state.lastSeenTimestamp > 0) {
      const newIds = notifications
        .filter(n => n.unixtimestamp > state.lastSeenTimestamp)
        .map(n => n.id)
      state.newNotificationIds = newIds
      state.newNotifications = newIds.length
    }
  },

  SET_LAST_VERSION (state, version) {
    state.lastVersion = version
  },

  SET_POLL_INTERVAL (state, interval) {
    state.pollInterval = interval
  },

  SET_IS_POLLING (state, value) {
    state.isPolling = value
  },

  SET_LOADING_MORE (state, value) {
    state.loadingMore = value
  },
}

const actions = {
  async fetchNotifications ({ commit, state }, groupIDs) {
    if (!groupIDs || groupIDs.length === 0) return

    const isFirstLoad = state.notifications.length === 0

    const response = await apiGetNotifications(groupIDs, PAGE_SIZE, 0)

    if (response && response.code === 1 && response.data) {
      const { notifications, hasMore } = response.data
      const list = Array.isArray(notifications) ? notifications : []
      commit('SET_NOTIFICATIONS', { notifications: list, hasMore })

      if (isFirstLoad && list.length > 0) {
        if (state.lastSeenTimestamp > 0) {
          commit('MARK_NEW_SINCE_LAST_SEEN', list)
        } else {
          // Primeiro acesso do usuário — marca todas como novas
          commit('ADD_NEW_NOTIFICATION_IDS', list.map(n => n.id))
          commit('SET_NEW_NOTIFICATIONS', list.length)
        }
      }
    }
  },

  async loadMore ({ commit, state }, groupIDs) {
    if (!groupIDs || groupIDs.length === 0 || !state.hasMore || state.loadingMore) return

    commit('SET_LOADING_MORE', true)

    const response = await apiGetNotifications(groupIDs, PAGE_SIZE, state.notifications.length)

    if (response && response.code === 1 && response.data) {
      const { notifications, hasMore } = response.data
      const list = Array.isArray(notifications) ? notifications : []
      commit('APPEND_NOTIFICATIONS_BOTTOM', { notifications: list, hasMore })
    }

    commit('SET_LOADING_MORE', false)
  },

  startPolling ({ commit, state }, groupIDs) {
    if (state.isPolling) return
    if (!groupIDs || groupIDs.length === 0) return

    commit('SET_IS_POLLING', true)

    const poll = async () => {
      const response = await apiPollNotifications(groupIDs, state.lastVersion)

      if (response && response.code === 1 && response.data) {
        const { version, notifications } = response.data

        if (notifications && notifications.length > 0) {
          const existingIds = new Set(state.notifications.map(n => n.id))
          const newOnly = notifications.filter(n => !existingIds.has(n.id))

          if (newOnly.length > 0) {
            commit('APPEND_NOTIFICATIONS_TOP', newOnly)
            commit('ADD_NEW_NOTIFICATION_IDS', newOnly.map(n => n.id))
            commit('SET_NEW_NOTIFICATIONS', state.newNotifications + newOnly.length)
          }
        }

        commit('SET_LAST_VERSION', version)
      }
    }

    const interval = setInterval(poll, POLL_INTERVAL_MS)
    commit('SET_POLL_INTERVAL', interval)
  },

  stopPolling ({ state, commit }) {
    if (state.pollInterval) {
      clearInterval(state.pollInterval)
      commit('SET_POLL_INTERVAL', null)
    }
    commit('SET_IS_POLLING', false)
  },

  markAllAsRead ({ commit }) {
    localStorage.setItem('@sos:notifications:lastSeenTs', String(Math.floor(Date.now() / 1000)))
    commit('RESET_NEW_NOTIFICATIONS')
  },
}

const getters = {
  recentNotifications: (state) => state.notifications.slice(0, 10),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
